/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { classNames } from 'primereact/utils';
import { CarboneScore as CarboneScoreModel } from '@/types/models.ts';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'primereact/tooltip';
import { carbonDescription } from '@/components/ProcessesWrapper/CaboneScore';

interface Props {
  score: CarboneScoreModel['score'];
  className?: string;
  id: string;
  help?: boolean;
  showUnknown?: boolean;
}

export const CarboneScore: React.FC<Props> = ({ score, className, id, help, showUnknown }) => {
  const { i18n } = useTranslation();
  const rateColors = {
    A: '#0e8a4a',
    B: '#73C82C',
    C: '#FBC707',
    D: '#F47216',
    E: '#EF3120'
  };

  const Letters = ({ score }: { score: CarboneScoreModel['score'] }) => {
    return (
      <div className={classNames(className, id, 'w-full cursor-auto')}>
        {Object.entries(rateColors).map(([key, v], index, array) => (
          <span
            key={key}
            style={{
              backgroundColor: score !== '?' ? v : '#d8d8d8',
              marginRight: key === score ? -2 : 0,
              marginLeft: key === score ? -2 : 0,
              zIndex: key === score ? 1000 : 0,
              color: score === '?' ? 'black' : key === score ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.7)'
            }}
            className={classNames(
              'inline-block p-1',
              { 'border-round-left': index === 0 && key !== score },
              { 'border-round-right': index === array.length - 1 && key !== score },
              { 'text-xs': key !== score },
              { 'border-2 border-round-xl relative font-bold text-md p-2': key === score }
            )}
          >
            {key}
          </span>
        ))}
      </div>
    );
  };
  return (
    <>
      {score === '?' ? (
        showUnknown ? (
          <Letters score={score} />
        ) : null
      ) : (
        <>
          <Letters score={score} />
          {help && (
            <Tooltip position="bottom" target={`.${id}`}>
              {carbonDescription[i18n.language]}
            </Tooltip>
          )}
        </>
      )}
    </>
  );
};
